import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Star, CheckCircle2, AlertTriangle, RefreshCw, Trash2, Loader2,
  KeyRound, Clock, ExternalLink,
} from "lucide-react";

export default function ClientTrustpilotPage() {
  const { clientId } = useParams();
  const [config, setConfig] = useState({ configured: false });
  const [connection, setConnection] = useState({ connected: false });
  const [businessUnitId, setBusinessUnitId] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const [cfg, conn] = await Promise.all([
        api.get("/trustpilot/config-status").then((r) => r.data).catch(() => ({ configured: false })),
        api.get(`/clients/${clientId}/trustpilot/connection`).then((r) => r.data).catch(() => ({ connected: false })),
      ]);
      setConfig(cfg); setConnection(conn);
      if (conn.business_unit_id) setBusinessUnitId(conn.business_unit_id);
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, [clientId]);

  const cooldownText = useMemo(() => {
    const t = connection.manual_sync_available_at;
    if (!t) return null;
    const diff = Math.max(0, Math.round(t - Date.now() / 1000));
    const m = Math.floor(diff / 60); const s = diff % 60;
    return `${m} min ${s.toString().padStart(2, "0")} s`;
  }, [connection.manual_sync_available_at]);

  const connect = async (e) => {
    e?.preventDefault?.();
    if (!businessUnitId.trim()) { toast.error("Business Unit ID required"); return; }
    setConnecting(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/trustpilot/connect`,
        { business_unit_id: businessUnitId.trim() });
      toast.success(`Connected · ${data.business_unit?.displayName || businessUnitId}`);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Connection failed");
    } finally { setConnecting(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Trustpilot?")) return;
    await api.delete(`/clients/${clientId}/trustpilot/disconnect`);
    toast.success("Disconnected"); await refresh();
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/trustpilot/sync`);
      toast.success(`${data.reviews_pulled} reviews · ${data.stars}★ · score ${data.trust_score?.toFixed(1)}`);
      await refresh();
    } catch (e) {
      const status = e.response?.status;
      const msg = e.response?.data?.detail || "Sync failed";
      if (status === 429) toast.warning(msg); else toast.error(msg);
    } finally { setSyncing(false); }
  };

  if (loading) return <div className="p-8 text-sm text-zinc-500">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="trustpilot-page">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 grid place-items-center rounded-sm bg-[#00B67A] text-white">
          <Star className="h-5 w-5 fill-white" />
        </div>
        <div>
          <div className="label-overline">Reviews</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">Trustpilot</h1>
          <p className="text-xs text-zinc-600 mt-1 max-w-2xl">
            One agency-level Trustpilot API key drives every client. Add the
            client&apos;s <b>Business Unit ID</b> below to connect. Manual sync runs on demand · 1 / hour.
          </p>
        </div>
      </div>

      {!config.configured && (
        <Card className="rounded-sm border-amber-200 bg-amber-50" data-testid="tp-setup-banner">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
            <div className="text-[12px] text-amber-900 flex-1">
              <div className="font-semibold mb-0.5">Trustpilot credentials not set yet</div>
              <div>
                Set <span className="font-mono">TRUSTPILOT_API_KEY</span> +{" "}
                <span className="font-mono">TRUSTPILOT_API_SECRET</span> in <code>backend/.env</code> and restart the backend.
                Get them at{" "}
                <a href="https://businessapp.b2b.trustpilot.com/integrations" target="_blank" rel="noreferrer"
                  className="underline">Trustpilot Business → Integrations <ExternalLink className="inline h-3 w-3" /></a>.
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-5 space-y-4">
          {connection.connected ? (
            <>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <Badge className="rounded-sm bg-emerald-100 text-emerald-800 border-emerald-200">
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Connected
                  </Badge>
                  <div>
                    <div className="font-bold text-lg">{connection.display_name || connection.business_unit_id}</div>
                    <div className="text-[11px] font-mono text-zinc-500">
                      BU {connection.business_unit_id}
                      {connection.last_sync_at && (
                        <> · Last sync: {new Date(connection.last_sync_at).toLocaleString()}</>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="rounded-sm bg-[#00B67A] hover:bg-[#009060] text-white"
                    disabled={syncing} onClick={sync}
                    data-testid="tp-sync">
                    {syncing
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                      : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                    Sync now
                  </Button>
                  <Button size="sm" variant="ghost" className="rounded-sm text-red-600"
                    onClick={disconnect} data-testid="tp-disconnect">
                    <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Disconnect
                  </Button>
                </div>
              </div>
              {connection.last_error && (
                <div className="rounded-sm border border-red-200 bg-red-50 p-2.5 text-[11px] font-mono text-red-800">
                  {connection.last_error}
                </div>
              )}
              {cooldownText && (
                <div className="text-[11px] text-zinc-500 flex items-center gap-1"
                  data-testid="tp-cooldown">
                  <Clock className="h-3 w-3" /> Next manual sync available in {cooldownText}.
                </div>
              )}
            </>
          ) : (
            <form onSubmit={connect} className="space-y-3">
              <div className="space-y-1.5">
                <Label className="label-overline">Trustpilot Business Unit ID</Label>
                <Input value={businessUnitId}
                  onChange={(e) => setBusinessUnitId(e.target.value)}
                  placeholder="e.g. 4a59c8f50000ff00050ac4d2"
                  className="rounded-sm font-mono text-xs h-9 max-w-[400px]"
                  data-testid="tp-bu-input" />
                <p className="text-[10px] text-zinc-500">
                  Find it at{" "}
                  <a href="https://businessapp.b2b.trustpilot.com/api-portal"
                    target="_blank" rel="noreferrer" className="underline">
                    Business App → API portal <ExternalLink className="inline h-3 w-3" />
                  </a>
                </p>
              </div>
              <Button type="submit" disabled={connecting || !config.configured}
                className="rounded-sm bg-[#00B67A] hover:bg-[#009060] text-white"
                data-testid="tp-connect">
                {connecting
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                  : <KeyRound className="h-3.5 w-3.5 mr-1.5" />}
                Connect with Trustpilot
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
